import React from 'react';

interface ConfirmModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    loading?: boolean;
    confirmDisabled?: boolean;
    // Optional labels so destructive actions can say "Cancel order", "Delete", etc.
    // Defaults to "Confirm" and "Cancel" so all existing usages continue to work unchanged.
    confirmLabel?: string;
    cancelLabel?: string;
    children?: React.ReactNode;
}

const ConfirmModal = ({isOpen, title, message, onConfirm, onCancel, loading = false, confirmDisabled = false, confirmLabel = 'Confirm', cancelLabel = 'Cancel', children,}: ConfirmModalProps) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/30 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-card">
                <div className="border-b border-stone-200 px-5 py-4">
                    <h3 className="font-display text-lg text-ink-900">{title}</h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">{message}</p>
                </div>

                {/* Optional slot for additional context — e.g. a warning banner */}
                <div className="px-5 py-4">
                    {children && (
                        <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
                            {children}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-stone-200 bg-stone-50 px-5 py-4">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="rounded-xl border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {cancelLabel}
                    </button>

                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading || confirmDisabled}
                        className="inline-flex items-center gap-2 rounded-xl bg-[#ee2b74] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[#d4245f] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {loading && (
                            <svg
                                className="h-4 w-4 animate-spin text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                        )}
                        <span>{loading ? 'Processing…' : confirmLabel}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;